import React from "react";
import { ScanLine, GaugeCircle, PackageSearch, ClipboardCheck, ArrowUpRight } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import { SectionContainer, SectionLabel, SectionHeading, SectionLead } from "./SectionPrimitives";

const TOOLS = [
  {
    icon: ScanLine,
    label: "Dealer Vehicle Scraper",
    code: "TOOL-01",
    desc: "Vollautomatischer Bestands-Crawler. Synchronisiert Fahrzeugdaten täglich aus DMS und Drittquellen.",
    image: "/screenshots/08_inventar.webp",
    caption: "Inventar-Sync · 2.143 Fahrzeuge, 12 Standorte, stündliches Delta",
  },
  {
    icon: GaugeCircle,
    label: "adsFUX Feed-Check",
    code: "TOOL-02",
    desc: "Pflichtfeld- und Validierungs-Engine. Score je Fahrzeug, Disapproval-Prävention, Audit-Trail.",
    image: "/screenshots/07_feed_check.webp",
    caption: "Feed-Service-Checkliste · Technische, Ads- und Feed-Voraussetzungen",
  },
  {
    icon: PackageSearch,
    label: "Anzeigen-Simulator",
    code: "TOOL-03",
    desc: "Live-Preview jeder Anzeige im Format Search-Card, Carousel, Mobile, Desktop und Display.",
    image: "/screenshots/06_dienstleister_overview.webp",
    caption: "Anzeigen-Vorschau mit Datenqualitäts-Check pro Fahrzeug",
  },
  {
    icon: ClipboardCheck,
    label: "Händler-Audit & Report",
    code: "TOOL-04",
    desc: "Strukturierter Befund mit Empfehlungen. Basis für Aufsatz, Anreicherung und SEO-Konfiguration.",
    image: "/screenshots/10_audit_report.webp",
    caption: "Audit-Report · Scoring je Standort, Marke und Datenfeld",
  },
];

export default function ToolProof() {
  return (
    <SectionContainer id="tool-proof">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <SectionLabel number="/ 05" label="Tool-Proof" />
          <SectionHeading>Werkzeuge, die unsere Prozesssicherheit beweisen.</SectionHeading>
          <SectionLead>
            Wir verkaufen keine Tools. Wir liefern eine Dienstleistung — und betreiben dafür
            eine eigene Infrastruktur. Diese Werkzeuge zeigen, mit welcher Tiefe wir arbeiten.
          </SectionLead>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.08}>
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <p className="label-mono text-slate-500">Eigene Infrastruktur</p>
              <p className="mt-2 text-sm text-slate-700">
                Crawler, Validierung, Anreicherung, Feed-Engine, SEO-Generator — alle Module
                greifen ineinander. Sie sehen Ergebnisse, wir betreiben das System.
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      <StaggerGroup className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
        {TOOLS.map((t, idx) => {
          const Icon = t.icon;
          return (
            <StaggerItem key={t.label}>
              <article data-testid={`tool-${idx}`} className="group overflow-hidden rounded-lg border border-slate-200 bg-white ring-soft transition-all hover:-translate-y-1 hover:border-slate-300">
                <div className="p-6 sm:p-7">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex size-10 items-center justify-center rounded-md border border-slate-200 bg-slate-50">
                      <Icon className="size-4 text-cyan-600" />
                    </span>
                    <span className="label-mono text-slate-400">{t.code}</span>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-slate-900 sm:text-xl">{t.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.desc}</p>
                </div>

                <div className="relative mx-6 mb-6 overflow-hidden rounded-md border border-slate-200 bg-slate-50 sm:mx-7 sm:mb-7">
                  <img
                    src={t.image}
                    alt={`${t.label} · UI-Vorschau`}
                    loading="lazy"
                    decoding="async"
                    className="block h-auto w-full transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white/95 via-white/40 to-transparent px-4 pb-3 pt-8">
                    <p className="font-mono text-[11px] text-slate-600">{t.caption}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between px-6 pb-6 sm:px-7 sm:pb-7">
                  <div className="inline-flex items-center gap-1.5 text-[12px] font-medium text-cyan-700">
                    Im Audit enthalten <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                  <span className="font-mono text-[11px] text-slate-400">Live-Modul · Q1/26</span>
                </div>
              </article>
            </StaggerItem>
          );
        })}
      </StaggerGroup>
    </SectionContainer>
  );
}
